import React, { useState } from "react";
import { Button, Grid2, Tooltip, Typography } from "@mui/material";
import { Cancel, Mail, Phone, Videocam } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { updateColleagues } from "../../../../utils/axios-requests";
import { useColleague } from "../../hooks/useColleague";
import { deleteColleague } from "./deleteColleague/deleteColleague";
import ConfirmDialog from "./ConfirmDialog/ConfirmDialog";
import { Colleague } from "../../types/Colleague.type";
import { useSnackbar } from "notistack";

type P = {
  /** original value of colleague */
  colleague: Colleague;
  /** editing colleague value */
  editedColleague: Colleague;
  setEditedColleague: (colleague: Colleague) => void;
  onRefetch: () => void;
};

export const ColleagueCardFooter: React.FC<P> = ({
  colleague,
  editedColleague,
  setEditedColleague,
  onRefetch
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { isInquiry } = useColleague(editedColleague);
  const commonIconSxProps = { color: "#A9A9A9", margin: "0 5px" };

  const [displayConfirmDialog, setDisplayConfirmDialog] = useState(false);

  const catchNotAllowToDisconnectErr = (err: any) => {
    /** do not allow to delete connection who is not allowed to due to auto connection rule */
    const notAllowToDisconnectByAutoConnectionRule =
      "Your connection changes are not aligned with relevant auto connection rule";

    if (err.response.data.message === notAllowToDisconnectByAutoConnectionRule) {
      enqueueSnackbar(t("_colleagueNotAllowToDisconnectByAutoConnectionRule"), {
        variant: "error"
      });
    } else enqueueSnackbar(err.response.data.message, { variant: "error" });
  };

  async function handleDelete() {
    await deleteColleague(editedColleague)
      .then(() => onRefetch())
      .catch(err => catchNotAllowToDisconnectErr(err));
  }

  function handleClickDelete() {
    setDisplayConfirmDialog(true);
  }

  function handleCancel() {
    setDisplayConfirmDialog(false);
  }

  return (
    <Grid2
      container
      direction={"row"}
      sx={{ width: "100%", marginBottom: "5px", marginTop: "5px" }}
      justifyContent={"flex-end"}
    >
      {!isInquiry && (
        <Grid2>
          {colleague.telephoneNumber && (
            <a href={`tel:${colleague.telephoneNumber}`}>
              <Phone sx={commonIconSxProps} />
            </a>
          )}
          {colleague.email && (
            <a href={`mailto:${colleague.email}`}>
              <Mail sx={commonIconSxProps} />
            </a>
          )}
          {colleague.videoID && <Videocam sx={commonIconSxProps} />}
          <Tooltip
            enterTouchDelay={0}
            title={<Typography>{t("Click to disconnect")}</Typography>}
            aria-label={"Click to disconnect"}
          >
            <Cancel
              data-testid="colleague-card-unfriend-btn"
              onClick={handleClickDelete}
              sx={{ color: "#E10000", cursor: "pointer", marginLeft: "3px" }}
            />
          </Tooltip>
          <ConfirmDialog
            isOpen={displayConfirmDialog}
            text={t("confirmUserDeletion", {
              firstName: colleague.firstName,
              lastName: colleague.surname
            })}
            onConfirm={handleDelete}
            onAbort={handleCancel}
            confirmButtonText={t("confirmUserDeletionButton")}
          />
        </Grid2>
      )}
      {isInquiry && (
        <Grid2 container justifyContent={"center"}>
          <Button
            data-testid="colleague-card-accept-btn"
            onClick={() => {
              const updatedUser = { ...editedColleague };
              updatedUser.friendsReqPermission = true;
              updatedUser.friendsPermission = true;
              if (updatedUser.teamBookingReqPermission) updatedUser.teamBookingPermission = true;
              if (updatedUser.scheduleReqPermission) updatedUser.schedulePermission = true;
              updateColleagues([updatedUser]).then(() => setEditedColleague(updatedUser));
            }}
            color={"primary"}
          >
            {t("Accept")}
          </Button>
          <Button
            data-testid="colleague-card-decline-btn"
            onClick={() => {
              const updatedUser = { ...editedColleague };
              if (!updatedUser.teamBookingPermission) {
                updatedUser.teamBookingReqPermission = false;
              }
              if (!updatedUser.schedulePermission) {
                updatedUser.scheduleReqPermission = false;
              }
              if (updatedUser.friendsReqPermission && updatedUser.friendsPermission) {
                updatedUser.friendsReqPermission = false;
              }
              if (
                updatedUser.scheduleColleague &&
                updatedUser.scheduleReqColleague &&
                updatedUser.teamBookingColleague &&
                updatedUser.teamBookingReqColleague
              ) {
                updateColleagues([updatedUser])
                  .then(() => setEditedColleague(updatedUser))
                  .catch(err => catchNotAllowToDisconnectErr(err));
              } else {
                handleDelete();
              }
            }}
            sx={{ marginLeft: 1 }}
            color={"secondary"}
          >
            {t("Decline")}
          </Button>
        </Grid2>
      )}
    </Grid2>
  );
};
